import "app/css/widgets/GridList.less";
import BaseWidget from "common/widgets/base/BaseWidget";
import locale from "common/managers/locale";
import DFValidator from "common/validator/DFValidator";
import DataSourceWidget from "app/widgets/GridList/DataSourceWidget";
import scroll from "common/helper/scroll";
import LoadingBar from "common/widgets/LoadingBar/LoadingBar";
import "app/widgets/DFButton/MenuButton";

function compileAttrs(attrs) {
    const compiledAttrs = {};
    if(attrs) {
        for(let attr in attrs) {
            compiledAttrs[attr] = kendo.template(attrs[attr]);
        }
    }
    return function(item, staticAttrs) {
        const attrs = {};
        for(let attr in compiledAttrs) {
            attrs[attr] = compiledAttrs[attr](item);
        }
        return {...attrs, ...staticAttrs};
    };
}

function compileRawCell(cellOpts) {
    const template = kendo.template(cellOpts.template || "");
    const attrs = compileAttrs(cellOpts.attrs);
    return function(item) {
        const $cell = $("<div>", attrs(item, {
            "role": "gridcell",
            "id": kendo.guid(),
        }));
        $cell.addClass("cell");
        $cell.html(template(item));
        return $cell;
    };
}

function commandDefaults(commandOpts) {
    if(commandOpts.name === "delete") {
        return {
            "event": "delete",
            "text": "delete",
            "icon": "c",
            "attrs": {
                "class": "closeButton"
            }
        }
    } else if(commandOpts.name === "edit") {
        return {
            "event": "startEdit",
            "text": "edit",
            "icon": "e",
            "attrs": {
                "class": "editButton"
            }
        }
    } else {
        return {};
    }
}

function compileCommand(commandOpts) {
    const adjustedCommandOpts = $.extend(true, commandDefaults(commandOpts), commandOpts);
    const attrs = compileAttrs(adjustedCommandOpts.attrs);
    const title = locale.get(adjustedCommandOpts.text);
    const text = adjustedCommandOpts.icon || title;
    return function(item) {
        const $button = $("<button>", attrs(item, {
            "id": kendo.guid(),
            "tabindex": "-1",
            "data-event": adjustedCommandOpts.event,
            "title": title
        }));
        const $span = $("<span>", {
            "text": text
        });
        if(adjustedCommandOpts.icon) {
            $span.attr("aria-hidden", "true");
        }
        $button.append($span);
        return $button;
    }
}

function compileLink(linkOpts) {
    const attrs = compileAttrs(linkOpts.attrs);
    return function(item) {
        return $("<a></a>", attrs(item, {
            "id": kendo.guid(),
            "tabindex": "-1"
        }));
    }
}

function menuDefaults() {
    return {
        "attrs": {
            "data-direction": "left"
        }
    };
}

function compileMenu(menuOpts) {
    const adjustedMenuOpts = $.extend(true, menuDefaults(), menuOpts);
    const attrs = compileAttrs(adjustedMenuOpts.attrs);
    return function(item) {
        return $("<button></button>", attrs(item, {
            "id": kendo.guid(),
            "tabindex": "-1",
            "data-role": "menubutton"
        }));
    }
}

function compileCell(cellOpts) {
    if(cellOpts.command) {
        const template = compileCommand(cellOpts.command);
        return compileRawCell({...cellOpts, template});
    } else if(cellOpts.link) {
        const template = compileLink(cellOpts.link);
        return compileRawCell({...cellOpts, template});
    } else if(cellOpts.menu) {
        const template = compileMenu(cellOpts.menu);
        return compileRawCell({...cellOpts, template});
    }
    return compileRawCell(cellOpts);
}

function compileCellLine(cellContainerOpts) {
    const cells = cellContainerOpts.map(cellOpts => compileCell(cellOpts));
    return function(item) {
        const $row = $("<div>", {
            "role": "presentation"
        });
        $row.addClass("cellContainer");
        cells.forEach(function(cell) {
            $row.append(cell(item));
        });
        return $row;
    };
}

function compile(listItemOpts, cellOpts) {
    const attrs = compileAttrs(listItemOpts.attrs);
    const children = cellOpts.map(function(cellOrLineOpts) {
        if(cellOrLineOpts.length) {
            return compileCellLine(cellOrLineOpts);
        } else {
            return compileCell(cellOrLineOpts);
        }
    });
    return function(item) {
        const $item = $("<div>", attrs(item,{
            "role": "row",
            "data-uid": item.uid
        }));
        $item.addClass("gridList-item");
        children.forEach(function(child) {
            $item.append(child(item));
        });
        $item.attr("aria-owns", $item.find("[role='gridcell']").toArray().map(el => $(el).attr("id")).join(" "));
        return $item;
    };
}

/**
 * This class is responsible for executing grid commands.
 */
class CommandSupport {
    constructor($grid, widget) {
        this.widget = widget;
        this.$grid = $grid;
        this.bind();
    }

    bind() {
        this.$grid.on("keydown.GridCommandSupport", (e) => {
            if(e.keyCode === kendo.keys.SPACEBAR || e.keyCode === kendo.keys.ENTER) {
                const id = this.$grid.attr("aria-activedescendant");
                const button = this.$grid.find(`#${id}[data-event]`);
                if(button.length) {
                    this.execute(button);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        });
        this.$grid.on("click.GridCommandSupport", "button[data-event]", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.execute($(e.currentTarget));
        });
    }

    execute(button) {
        this.widget.focus(); //Focus is critical for NVDA support. It is possible while NVDA is running for focus to move to the actual button. This can break all sorts of logic. Correct that before proceeding.
        const event = button.attr("data-event");
        const row = button.closest("[role='row'][data-uid]");
        const model = this.widget.dataItem(row);
        this.widget.trigger(event, {
            model: model
        });
    }

    unbind() {
        this.$grid.off(".GridCommandSupport");
    }
}

/**
 * This class is responsible for navigating grid links.
 */
class LinkSupport {
    constructor($grid) {
        this.$grid = $grid;
        this.bind();
    }

    bind() {
        this.$grid.on("keydown.GridLinkSupport", (e) => {
            if(e.keyCode === kendo.keys.SPACEBAR || e.keyCode === kendo.keys.ENTER) {
                const id = this.$grid.attr("aria-activedescendant");
                const link = this.$grid.find(`a#${id}`);
                if(link.length) {
                    this._click(link[0], e);
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        });
    }

    _click(element, e) {
        element.dispatchEvent(new MouseEvent("click", {
            button: 0,
            altKey: e.altKey,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey,
            metaKey: e.metaKey,
            cancelable: true
        }));
    }

    unbind() {
        this.$grid.off(".GridLinkSupport");
    }
}

/**
 * This class is responsible for opening grid menus.
 */
class MenuSupport {
    constructor($grid, widget) {
        this.widget = widget;
        this.$grid = $grid;
        this.bind();
    }

    bind() {
        this.$grid.on("keydown.GridMenuSupport", (e) => {
            if(e.keyCode === kendo.keys.SPACEBAR || e.keyCode === kendo.keys.ENTER) {
                const id = this.$grid.attr("aria-activedescendant");
                const menu = this.$grid.find(`button[data-role="menubutton"]#${id}`);
                if(menu.length) {
                    const widget = kendo.widgetInstance(menu);
                    widget.popup.open();
                    widget.popup.one("activate", () => {
                        widget.focus();
                    });
                    widget.popup.one("close", () => {
                        this.widget.focus();
                    });
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        });
    }

    unbind() {
        this.$grid.off(".GridMenuSupport");
    }
}

/**
 * This class is responsible for the editiable support.
 */
class EditableSupport {

    constructor($grid, widget) {
        this.$grid = $grid;
        this.widget = widget;
        this.editing = false;
        this.bind()
    }

    setOptions(editTemplate) {
        this.template = kendo.template(editTemplate || "");
    }

    bind() {
        this._startEdit = (e) => {
            this.edit(`div[data-uid="${e.model.uid}"]`);
        };
        this.widget.bind("startEdit", this._startEdit);
        this._considerRefresh = (e) => {
            if(this.editing && (!e || !e.originalEvent || e.originalEvent.action !== "sync")) {
                e.preventDefault();
            }
        };
        this.widget.bind("considerRefresh", this._considerRefresh);
        this.$grid.on("click.EditableSupport", ".editForm .k-update-button", () => {
            this.save();
        });
        this.$grid.on("click.EditableSupport", ".editForm .k-cancel-button", () => {
            this.cancel();
        });
        this.$grid.on("keydown.EditableSupport", ".editForm", (e) => {
            if(e.keyCode === kendo.keys.ENTER && !$(e.target).is("button")) {
                e.stopPropagation();
                this.save();
            }
        });
    }

    unbind() {
        this.widget.unbind("startEdit", this._startEdit);
        this.widget.unbind("considerRefresh", this._considerRefresh);
        this.$grid.off(".EditableSupport")
    }

    edit(row) {
        const item = $(row);
        this.model = this.widget.dataItem(row);
        if(!this.editing && this.model && !this.widget.trigger("beforeEdit", {model: this.model})) {
            this.editing = true;
            this.widget.trigger("dataBinding", {
                removedItems: item
            });
            this.container = $(this.template(this.model));
            this.container.attr("role", "form");
            this.container.attr("data-uid", this.model.uid);
            this.container.addClass("editForm");
            this.container.insertAfter(item);
            item.remove();
            this.validator = new DFValidator(this.container, {});
            this.widget.trigger("dataBound", {
                addedItems: this.container,
                addedDataItems: [this.model]
            });
            this.widget.trigger("edit", {model: this.model, container: this.container});
        }
    }

    cancel() {
        if(this.editing && !this.widget.trigger("cancel", {container: this.container, model: this.model})) {
            const rowIndex = this.widget.dataSource.indexOf(this.model);
            this._endEditing();
            this.widget.dataSource.cancelChanges();
            this.widget.focusCell(rowIndex, "edit");
        }
    }

    _endEditing() {
        this.editing = false;
        delete this.model;
        delete this.container;
        delete this.validator;
        this.widget.trigger("afterEdit");
    }

    save() {
        if(this.editing && !this.widget.trigger("save", {model: this.model, item: this.container}) && this.validator.validate()) {
            const model = this.model;
            const $saveButton = this.container.find(".k-update-button");
            const saveButton = kendo.widgetInstance($saveButton);
            const saved = this.widget.dataSource.sync()
                .done(() => {
                    this._endEditing();
                    this.widget.focusCell(this.widget.dataSource.indexOf(model), "edit")
                });
            saveButton.value(saved);
            return saved;
        }
    }
}

/**
 * This class is responsible for the aria-activedescendant, the "focusedCell" class and movement keyboard support.
 */
class NavigationSupport {

    constructor($grid, widget) {
        this.$grid = $grid;
        this.widget = widget;
        this._cellsPerRow = 0;
        this._cellReferences = [];
        this._rowReferences = [];
        this.bind();
    }

    bind() {
        this.$grid.on("focus.GridFocusSupport", () => {
            this.moveIfNeeded(0);
            this.updateRowFocus();
        });
        this.$grid.on("blur.GridFocusSupport", () => {
            this.updateRowFocus();
        });
        this.$grid.on("keydown.GridFocusSupport", (e) => {
            if(e.keyCode === kendo.keys.DOWN) {
                this.move(this._cellsPerRow);
                e.preventDefault();
            } else if(e.keyCode === kendo.keys.UP) {
                this.move(-1 * this._cellsPerRow);
                e.preventDefault();
            } else if(e.keyCode === kendo.keys.LEFT) {
                this.move(-1);
                e.preventDefault();
            } else if(e.keyCode === kendo.keys.RIGHT) {
                this.move(1);
                e.preventDefault();
            }
        });
        this.$grid.on("mousedown.GridFocusSupport", "[role='gridcell'][id]", (e) => {
            this.moveToId($(e.currentTarget).attr("id"));
        });
        this.$grid.on("animationend.GridFocusSupport", "[role='row']", e => {
            this.widget._reallyRemove(e.currentTarget);
        });
    }

    unbind() {
        this.$grid.off(".GridFocusSupport");
    }

    refresh(e, render) {
        let position = 0;
        if(e?.action === "itemchange" || e?.action === "sync") {
            position = this.findFocus();
        } else if(e?.action === "remove" && e?.items?.length === 1 && this.widget._delayedRemove(e)) {
            position = (this._rowReferences.indexOf(e.items[0].uid) || 0) * this._cellsPerRow;
        } else if(e?.action === "really_remove") {
            const row_index = this._rowReferences.indexOf(e.items[0].uid)
            const start_of_removed_row = row_index * this._cellsPerRow;
            const now = this.findFocus();
            if(row_index === -1 || now < start_of_removed_row) {
                position = now;
            } else {
                position = now - this._cellsPerRow;
            }
        }
        render();
        this._rowReferences = this.$grid.children("[role='row']")
            .toArray()
            .map(el => $(el).attr("data-uid"));
        const rowCount = this._rowReferences.length;
        this._cellReferences = this.$grid.find("[role='gridcell']")
            .toArray()
            .map(function(el) {
                const $el = $(el);
                const activeTarget = $el.find("button[id][data-event],a[id],button[data-role='menubutton']");
                if(activeTarget.length === 1) {
                    return activeTarget[0];
                } else {
                    return el;
                }
            })
            .map(el => $(el).attr("id"));
        const cellCount = this._cellReferences.length;
        this._cellsPerRow = cellCount/rowCount; //Even hidden items still appear in the DOM, so this will always be accurate.
        if(this.$grid[0] === document.activeElement) {
            //if the grid is currently focused, something needs to be focused
            this.moveIfNeeded(position);
        }
    }

    moveToPosition(pos, getNextPos) {
        if(pos >= 0 && pos < this._cellReferences.length) {
            const success = this.moveToId(this._cellReferences[pos]);
            if(!success) {
                this.moveToPosition(getNextPos(pos), getNextPos);
            }
        }
    }

    moveIfNeeded(pos) {
        if(!this.haveValidFocus()) {
            this.moveToPosition(pos, i => i + 1);
        }
    }

    moveToId(id) {
        const $cell = this.$grid.find(`#${id}`).closest("[role='gridcell']");
        if($cell.length === 1 && $cell.is(":visible")) {
            this.$grid.find(".focusedCell[role='gridcell']").removeClass("focusedCell");
            $cell.addClass("focusedCell");
            this.$grid.attr("aria-activedescendant", id);
            scroll.scrollIntoViewIfNeeded($cell[0], document.body);
            this.updateRowFocus();
            return true;
        } else {
            return false;
        }
    }

    updateRowFocus() {
        const itemFC = this.widget.options.listItem.focusedClass;
        if(itemFC) {
            this.$grid.find(`.${itemFC}`).removeClass(itemFC);
            if(this.$grid[0] === document.activeElement) {
                this.$grid.find(`.focusedCell[role="gridcell"]`)
                    .closest(`[role="row"]`)
                    .addClass(itemFC)
            }
        }
    }

    move(amount) {
        let posSearched = false;
        let seekNextRow = Math.abs(amount) > 1; // on large jumps, same row is best
        const best = this.findFocus() + amount;
        this.moveToPosition(best, i => {
            const changeOnFailure = amount >= 0 ? 1 : -1; //move in same direction as requested
            if(seekNextRow) {
                //get the limits of the current row
                const rowStart = i - (i % this._cellsPerRow);
                const rowEnd = rowStart + this._cellsPerRow - 1;
                //move in positive direction first
                if(!posSearched) {
                    const posChange = i + changeOnFailure;
                    if(posChange >= rowStart && posChange <= rowEnd) {
                        return posChange;
                    }
                    posSearched = true;
                }
                //If nothing is found in positive direction, work in the negative direction
                //This may "double up" on some checks, but they are guaranteed misses
                const negChange = i - changeOnFailure;
                if(negChange >= rowStart && negChange <= rowEnd) {
                    return negChange;
                }
                //Despite best efforts, there is nothing available on the next row. (Everything is invisible?!?)
                //In this case, fallback to a row skip by just moving the right direction
                seekNextRow = false;
                return best + changeOnFailure;
            } else {
                return i + changeOnFailure;
            }
        });
    }

    findFocus(defaultPos = 0) {
        const now = this.$grid.attr("aria-activedescendant");
        const pos = this._cellReferences.indexOf(now);
        return pos >= 0 ? pos : defaultPos;
    }

    haveValidFocus() {
        const pos = this.findFocus(null);
        return pos !== null && $(`#${this._cellReferences[pos]}`).is(":visible");
    }

    moveToRow(index, column) {
        this.moveToPosition(index * this._cellsPerRow + column, i => i + 1);
    }
}

const GridList = BaseWidget(kendo.ui.Widget).extend(DataSourceWidget).extend({

    options: {
        name: "GridList",
        dataSource: [],
        autoBind: true,
        filtersCount: 0,
        listItem: {
            removeClass: null,
            focusedClass: null,
            attrs: {}
        },
        editTemplate: "",
        cells: [
            [
                {
                    template: "",
                    attrs: {}
                }
            ]
        ]
    },

    events: [
        "dataBinding",
        "dataBound",
        "contextMenu",
        "save",
        "cancel",
        "edit",
        "beforeEdit",
        "afterEdit",
        "delayRemove"
    ],

    init: function() {
        kendo.ui.Widget.fn.init.apply(this, arguments);
        if(!this.element.attr("id")) {
            throw new Error("GridList widget requires an id");
        }
        this.wrapper = this.element;
        this.element
            .addClass("gridList")
            .attr("role", "grid")
            .attr("aria-readonly", "true")
            .attr("tabindex", "0");
        this._navigation = new NavigationSupport(this.element, this);
        this._commands = new CommandSupport(this.element, this);
        this._links = new LinkSupport(this.element);
        this._menus = new MenuSupport(this.element, this);
        this._editable = new EditableSupport(this.element, this);
        const loadingElement = $("<div>", {
            id: this.element.attr("id") + "_loading"
        });
        this.element.append(loadingElement);
        this._loadingBar = new LoadingBar(loadingElement, {});
        this._initOptions();
        this.element.on("contextmenu.GridList", "[role='row']", (e) => {
            this.trigger("contextMenu", {
                model: this.dataItem(e.currentTarget)
            });
        });
    },

    setOptions(options) {
        kendo.ui.Widget.fn.setOptions.apply(this, arguments);
        this._initOptions();
        this.refresh();
    },

    _initOptions() {
        this.initRender();
        this._editable.setOptions(this.options.editTemplate);
        this.setDataSource(this.options.dataSource);
    },

    initRender() {
        this._renderItem = compile(this.options.listItem, this.options.cells);
    },

    focus() {
        this.element.trigger("focus");
    },

    focusCell(row, cellName) {
        const cellNames = [].concat.apply([], this.options.cells)
            .map(cell => cell.command ? cell.command.name : cell.name);
        this._navigation.moveToRow(row, cellNames.indexOf(cellName));
        this.focus();
    },

    dataItem(selector) {
        return this.dataSource.getByUid($(selector).attr("data-uid"));
    },

    items() {
        return this.element.children("[data-uid]");
    },

    getItem(dataItem) {
        return this.element.children(`[data-uid="${dataItem.uid}"]`);
    },

    edit(item) {
        this._editable.edit(item);
    },

    save() {
        this._editable.save();
    },

    cancel() {
        this._editable.cancel();
    },

    _noData() {
        this.items().remove();
        this.element.addClass("noData");
    },

    loading(isLoading) {
        this.element.toggleClass("gridListLoading", !!isLoading);
        this._loadingBar.value(!!isLoading);
    },

    enable(enable) {
        this.wrapper.find("button[data-event]").prop("disabled", !enable);
    },

    _refresh(e) {
        this._navigation.refresh(e, () => {
            this.items().remove();
            this.element.removeClass("noData");
            this.dataItems().forEach((model) => {
                this.element.append(this._renderItem(model));
            });
        });
    },

    _minimalRefresh() {
        return !!this.options.listItem.removeClass;
    },

    _delayedRemove() {
        return !!this.options.listItem.removeClass && !this.trigger("delayRemove");
    },

    _itemPrepareRemove(data, e) {
        this._navigation.refresh(e, () => {
            const row = this.getItem(data);
            row.addClass(this.options.listItem.removeClass).attr("data-removed", "true")
        });
    },

    _reallyRemove(row) {
        this.trigger("dataBinding", {
            removedItems: [row]
        });
        this._navigation.refresh({
            action: "really_remove",
            items: [{
                uid: $(row).attr("data-uid")
            }]
        }, () => {
            row.remove();
        });
        this.trigger("dataBound", {
            addedItems: [],
            addedDataItems: []
        });
    },

    _itemAdd(index, data, e) {
        this._navigation.refresh(e, () => {
            this.element.removeClass("noData");
            const item = this._renderItem(data);
            const current = this.items().eq(index);
            if(current.length === 0) {
                this.element.append(item);
            } else {
                current.before(item);
            }
        });
    },

    _itemChange(data, e) {
        this._navigation.refresh(e, () => {
            this.getItem(data).replaceWith(this._renderItem(data));
        });
    },

    destroy: function() {
        kendo.ui.Widget.fn.destroy.apply(this, arguments);
        this.cleanupDS();
        if(this._navigation) {
            this._navigation.unbind();
            delete this._navigation;
        }
        if(this._commands) {
            this._commands.unbind();
            delete this._commands;
        }
        if(this._links) {
            this._links.unbind();
            delete this._links;
        }
        if(this._menus) {
            this._menus.unbind();
            delete this._menus;
        }
        if(this._editable) {
            this._editable.unbind();
            delete this._editable;
        }
        if(this._loadingBar) {
            this._loadingBar.destroy();
            delete this._loadingBar;
        }
        delete this.wrapper;
    }
});
kendo.ui.plugin(GridList);
export default GridList;
